import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CustomizationWorkspace = ({ policy, onPolicyUpdate }) => {
  const [editingClause, setEditingClause] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [validationError, setValidationError] = useState('');

  if (!policy) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 h-full flex items-center justify-center">
        <div className="text-center">
          <Icon name="FileText" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <h3 className="font-heading font-semibold text-foreground mb-2">No Policy Selected</h3>
          <p className="text-sm text-muted-foreground">
            Select a policy from the template library to begin customization
          </p>
        </div>
      </div>
    );
  }

  const handleEditClause = (clause) => {
    setEditingClause(clause?.id);
    setEditValue(clause?.current);
    setValidationError('');
  };

  const handleSaveClause = (clauseId) => {
    const clause = policy?.clauses?.find(c => c?.id === clauseId);
    
    // Validation: ensure customization is above minimum
    if (clause?.customizable) {
      // Simple validation - in real app, would parse and compare values
      if (!editValue || editValue?.trim() === '') {
        setValidationError('Value cannot be empty');
        return;
      }
    }

    // Update policy
    const updatedPolicy = {
      ...policy,
      clauses: policy?.clauses?.map(c =>
        c?.id === clauseId ? { ...c, current: editValue } : c
      )
    };

    onPolicyUpdate(updatedPolicy);
    setEditingClause(null);
    setEditValue('');
    setValidationError('');
  };

  const handleCancelEdit = () => {
    setEditingClause(null);
    setEditValue('');
    setValidationError('');
  };

  const handleResetToBaseline = (clauseId) => {
    const clause = policy?.clauses?.find(c => c?.id === clauseId);
    const updatedPolicy = {
      ...policy,
      clauses: policy?.clauses?.map(c =>
        c?.id === clauseId ? { ...c, current: c?.minimum } : c
      )
    };
    onPolicyUpdate(updatedPolicy);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 h-full">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="Edit" size={20} color="var(--color-primary)" />
        <h3 className="font-heading font-semibold text-foreground">Customization Wizard</h3>
      </div>
      {/* Policy Header */}
      <div className="mb-6 p-4 bg-muted rounded-lg">
        <div className="flex items-start justify-between mb-2">
          <h4 className="font-heading font-bold text-lg text-foreground">{policy?.name}</h4>
          {policy?.statutoryBaseline && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success text-xs rounded-full">
              <Icon name="Shield" size={12} />
              PNG Statutory Baseline
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">{policy?.description}</p>
      </div>
      {/* Clauses */}
      <div className="space-y-4 max-h-[600px] overflow-y-auto">
        {policy?.clauses?.map((clause, index) => {
          const isEditing = editingClause === clause?.id;
          const isModified = clause?.current !== clause?.minimum;

          return (
            <div
              key={clause?.id}
              className={`p-4 rounded-lg border-2 ${
                isModified ? 'border-primary bg-primary/5' : 'border-border bg-card'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground">Clause {index + 1}</span>
                  {isModified && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                      <Icon name="Edit2" size={10} />
                      Modified
                    </span>
                  )}
                  {!clause?.customizable && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full">
                      <Icon name="Lock" size={10} />
                      Fixed
                    </span>
                  )}
                </div>
              </div>
              <h5 className="font-medium text-foreground mb-2">{clause?.title}</h5>
              {!isEditing ? (
                <>
                  <p className="text-sm text-foreground mb-3">{clause?.content}</p>
                  
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="p-2 bg-muted rounded">
                      <p className="text-xs text-muted-foreground mb-1">Statutory Minimum</p>
                      <p className="text-sm font-medium text-foreground">{clause?.minimum}</p>
                    </div>
                    <div className="p-2 bg-muted rounded">
                      <p className="text-xs text-muted-foreground mb-1">Current Value</p>
                      <p className="text-sm font-medium text-foreground">{clause?.current}</p>
                    </div>
                  </div>

                  {clause?.customizable && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditClause(clause)}
                        iconName="Edit2"
                        iconPosition="left"
                      >
                        Customize
                      </Button>
                      {isModified && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResetToBaseline(clause?.id)}
                          iconName="RotateCcw"
                          iconPosition="left"
                        >
                          Reset to Baseline
                        </Button>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="mb-3">
                    <Input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e?.target?.value)}
                      error={validationError}
                      placeholder="Enter new value"
                    />
                  </div>

                  <div className="p-3 bg-warning/10 border border-warning rounded-md mb-3">
                    <div className="flex items-start gap-2">
                      <Icon name="AlertTriangle" size={14} color="var(--color-warning)" className="mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-warning mb-1">Compliance Validation</p>
                        <p className="text-xs text-muted-foreground">
                          Ensure modifications maintain legal adherence. Minimum requirement: {clause?.minimum}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleSaveClause(clause?.id)}
                      iconName="Check"
                      iconPosition="left"
                    >
                      Save Changes
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelEdit}
                      iconName="X"
                      iconPosition="left"
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-4 p-3 bg-primary/10 border border-primary rounded-md">
        <div className="flex items-start gap-2">
          <Icon name="Lightbulb" size={14} color="var(--color-primary)" className="mt-0.5" />
          <div>
            <p className="text-xs font-medium text-primary mb-1">Customization Guidance</p>
            <p className="text-xs text-muted-foreground">
              Customizable clauses can be adjusted above statutory minimums. Fixed clauses are mandated by PNG Employment Act and cannot be modified.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomizationWorkspace;